import { I18n } from '@calendly/i18n';
import { Badge } from '@calendly/ui/components/badge';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { Icon } from '@calendly/ui/components/icon';
import { IconButton } from '@calendly/ui/components/icon-button';
import { CopyIcon, DeleteIcon, DownloadIcon, EditPencilIcon, SettingsMenuVerticalIcon as SettingsIcon, UserSingleIcon } from '@calendly/ui/components/icons';
import { BareItem, Group, Item } from '@calendly/ui/components/menu';
import { SwitchInput } from '@calendly/ui/components/switch-input';
import classNames from 'classnames';
import { forwardRef, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ViewportDropdownMenu } from 'components/core/viewport_dropdown_menu';
import { EventTypeDeletePopup } from 'components/event_type_delete_popup';
import { ChangeLanguageItem, ChangeLanguageModal, InternalNotePopup, ToggleVisibilityItem } from 'components/event_type_edit';
import { Segment } from 'core/analytics/segment';
import { useEventType } from 'features/event_type_provider';
import { isObjectLevelPermissionsAvailableSelector, openEditPermissionsModal } from 'features/object_level_permissions';
import { useLazyGetEventTypeActivityReportQuery } from 'features/scheduling';
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events';
import { usePopupSwitcher } from 'hooks/use_popup_switcher';
import { useCloneEventTypeMutation, useDeleteEventTypeMutation } from './api';
import { useStateTrigger } from './state';
import { useActivateEventTypeMutation, useDeactivateEventTypeMutation, useSaveEventTypeInternalNoteMutation, useUpdateEventTypeMutation } from '../../../../api';
import { useEventTypeEditorSidebar } from '../../../../hooks';
const switchCss = "s87ug1w";
const Switch = forwardRef((props, ref) => <SwitchInput ref={ref} {...props} className={classNames(switchCss, props.className)} />);
const containerCss = "cjam6mb";
const Container = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(containerCss, props.className)} />;
});
const gearButtonCss = "gwke7xs";
const GearButton = forwardRef((props, ref) => <IconButton ref={ref} {...props} className={classNames(gearButtonCss, props.className)} />);
const switchItemCss = "sx4mdqh";
const SwitchItem = forwardRef((props, ref) => {
  const as = 'label';
  return <BareItem ref={ref} {...props} as={as} className={classNames(switchItemCss, props.className)} />;
});
const withTracking = (event, funct) => (...args) => {
  funct(...args);
  Segment.track(event);
};
const CloneButton = ({
  eventTypeId,
  onCloneEventType,
  isLoading
}) => {
  const cloneEventTypeHandler = () => {
    onCloneEventType(eventTypeId);
  };
  return <Item onClick={withTracking('event_type.clone.click', cloneEventTypeHandler)} disabled={isLoading}>
      <CopyIcon />
      {I18n.t('publisher.labels.clone')}
    </Item>;
};
export const MoreDropdown = ({
  hideSecretToggleLpMigr,
  isNameLocked = false
}) => {
  const dispatch = useDispatch();
  const eventType = useEventType();
  const {
    openPopup,
    closePopup,
    isPopupOpen
  } = usePopupSwitcher();
  const [toggleDropdown, Wrapper, isExpanded] = useDropdown();
  const isObjectLevelPermissionsAvailable = useSelector(isObjectLevelPermissionsAvailableSelector);
  const {
    triggerFunction
  } = useStateTrigger();
  const track = useTrack();
  const {
    id: openedEventTypeId,
    openSidebar: openEventTypeSidebar,
    closeSidebar: closeEventTypeSidebar
  } = useEventTypeEditorSidebar();
  const {
    name,
    active,
    type,
    id: eventTypeId,
    internal_note: internalNote,
    owner_id: etOwnerId,
    owner_type: ownerType,
    public: isPublic,
    policy: {
      edit: canEdit,
      clone: canClone,
      export: canExport,
      destroy: canDestroy
    },
    profile,
    isMETChild
  } = eventType;
  const [saveInternalNote] = useSaveEventTypeInternalNoteMutation();
  const [cloneEventType, {
    isLoading: isCloneLoading
  }] = useCloneEventTypeMutation();
  const [getActivityReport] = useLazyGetEventTypeActivityReportQuery();
  const [deleteEventType, {
    isSuccess: deleteIsSuccess
  }] = useDeleteEventTypeMutation();
  const [updateEventType, {
    isSuccess,
    isLoading
  }] = useUpdateEventTypeMutation({
    fixedCacheKey: `update-event-type-attributes-${eventTypeId}`
  });
  const [activate, {
    isLoading: isActivating
  }] = useActivateEventTypeMutation({
    fixedCacheKey: `activate-event-type-${eventTypeId}`
  });
  const [deactivate, {
    isLoading: isDeactivating
  }] = useDeactivateEventTypeMutation({
    fixedCacheKey: `deactivate-event-type-${eventTypeId}`
  });
  const isToggleLoading = isActivating || isDeactivating;
  const isTeam = ownerType === 'Page';
  const canEditPermissions = isObjectLevelPermissionsAvailable && !isTeam;
  const ownerId = etOwnerId || profile?.owner_id;
  const saveInternalNoteHandler = value => saveInternalNote({
    eventType,
    internalNote: value
  });
  const updateLanguageHandler = ({
    attributes
  }) => updateEventType({
    eventType,
    attributes: {
      locale: attributes.locale
    },
    successMessage: I18n.t('publisher.event_type_editor.header.settings.invitee_language.language_updated'),
    updateCacheAttributes: ['locale']
  });
  const visibilityToggleHandler = visible => updateEventType({
    eventType,
    attributes: {
      public: visible
    },
    successMessage: I18n.t('publisher.event_type_editor.header.settings.visibility.changed'),
    updateCacheAttributes: ['public']
  });
  const cloneEventTypeHandler = etId => cloneEventType({
    ownerId,
    ownerType,
    eventTypeId: etId,
    onSuccess: ({
      id
    }) => openEventTypeSidebar({
      id,
      type,
      ownerType,
      ownerId
    })
  });
  const deleteEventTypeHandler = useCallback(() => {
    deleteEventType({
      ownerId,
      ownerType,
      eventTypeId,
      openedEventTypeId,
      closeEventTypeSidebar
    });
  }, [closeEventTypeSidebar, deleteEventType, eventTypeId, openedEventTypeId, ownerId, ownerType]);
  const getActivityReportHandler = () => getActivityReport({
    eventTypeId
  });
  const toggleActivationHandler = event => {
    if (event.target.checked) {
      activate({
        eventType
      });
    } else {
      deactivate({
        eventType
      });
    }
  };
  const openEditPermissionsModalHandler = () => dispatch(openEditPermissionsModal(eventType));
  const triggerRename = () => {
    if (triggerFunction) triggerFunction('rename');
  };
  useEffect(() => {
    if (deleteIsSuccess) {
      closePopup();
    }
  }, [deleteIsSuccess, closePopup]);
  const hasTrackedExpansion = useRef(false);
  useEffect(() => {
    if (isExpanded && !hasTrackedExpansion.current) {
      track({
        name: TRACKING_EVENT_NAMES.DROPDOWN_OPENED,
        properties: {
          location: 'event_type_editor',
          name: 'kebab_menu'
        }
      });
      hasTrackedExpansion.current = true;
    }
  }, [isExpanded, track]);
  return <Container>
      <GearButton aria-expanded={isExpanded} title={I18n.t('publisher.event_types.context.toggle_button', {
      name
    })} onClick={toggleDropdown} data-calendly-label="et-settings-cog">
        <SettingsIcon />
      </GearButton>
      <Wrapper>
        <ViewportDropdownMenu fullWidth viewportOffset={{
        top: -32
      }}>
          <Group>
            {canEdit && <ChangeLanguageItem eventType={eventType} onClick={withTracking('event_type.change_language.click', openPopup('change_language'))} overrideDisabled />}
            {canEdit && <Item onClick={withTracking('event_type.internal_note.click', openPopup('internal_note'))}>
                <Icon name="sticky-note" />
                {internalNote ? I18n.t('publisher.event_types.internal_note.edit') : I18n.t('publisher.event_types.internal_note.add')}
              </Item>}
            {canEdit && !hideSecretToggleLpMigr && <ToggleVisibilityItem isPublic={isPublic} onToggle={withTracking('event_type.visibility_toggle.click', visibilityToggleHandler)} />}
            {canEditPermissions && <Item onClick={openEditPermissionsModalHandler}>
                <UserSingleIcon />
                {I18n.t('publisher.event_types.edit_permissions')}
                <Badge type="new">{I18n.t('publisher.components.badge.new')}</Badge>
              </Item>}
          </Group>
          {(!isMETChild || isMETChild && !isNameLocked) && <Group>
              <Item onClick={withTracking('event_type.rename.click', triggerRename)}>
                <EditPencilIcon />
                {I18n.t('publisher.event_type_editor.sections.name.settings_menu.rename')}
              </Item>
              {canClone && <CloneButton eventTypeId={eventTypeId} onCloneEventType={cloneEventTypeHandler} isLoading={isCloneLoading} />}
              {canExport && isTeam && <Item onClick={getActivityReportHandler}>
                  <DownloadIcon />
                  {I18n.t('publisher.event_types.context.event_statistics')}
                </Item>}
              {canDestroy && <Item onClick={withTracking('event_type.delete.click', openPopup('delete_popup'))}>
                  <DeleteIcon />
                  {I18n.t('publisher.action.delete')}
                </Item>}
            </Group>}
          {canEdit && <Group>
              <SwitchItem>
                {I18n.t('publisher.event_types.context.on_off')}
                <Switch checked={active} onChange={isToggleLoading ? () => {} : withTracking('event_type.toggle.click', toggleActivationHandler)} />
              </SwitchItem>
            </Group>}
        </ViewportDropdownMenu>
      </Wrapper>
      <EventTypeDeletePopup isOpen={isPopupOpen('delete_popup')} name={name} onConfirm={deleteEventTypeHandler} onClose={closePopup} />
      {isPopupOpen('internal_note') && <InternalNotePopup isOpen={isPopupOpen('internal_note')} internalNote={internalNote} onConfirm={saveInternalNoteHandler} onClose={closePopup} />}
      {isPopupOpen('change_language') && <ChangeLanguageModal eventType={eventType} isOpen={isPopupOpen('change_language')} onClose={closePopup} updateEventType={updateLanguageHandler} isLoading={isLoading} isSuccess={isSuccess} />}
    </Container>;
};

require("./more_dropdown.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./more_dropdown.js");